import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DeskTable, StackedDateTime, muted, type Column } from '@/components/common/DeskTable'
import FacetFilter from '@/components/common/FacetFilter'
import FilterBarApply from '@/components/common/FilterBarApply'
import FilterBarForm from '@/components/common/FilterBarForm'
import FilterSearch from '@/components/common/FilterSearch'
import PageFrame from '@/components/common/PageFrame'
import { Button } from '@/components/ui/button'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import StatusBadge from '@/components/common/StatusBadge'
import { Sheet } from '@/components/ui/dialog'
import { backofficeApi, type FundQueueItem } from '@/lib/api-client'
import { emptyFacet, facetToQuery } from '@/lib/facet'
import { methodLabel, optionLabels, statusLabel } from '@/lib/labels'
import { dash, downloadCsv, fmtDateTime, fmtMoney, fmtTime, mtAccount, splitIb } from '@/lib/format'
import { fundColumnsForWidth, FUND_COLUMN_WIDTH } from '@/lib/fund-columns'
import { facetFromSearch, writeFacet, writeSort } from '@/lib/desk-url'
import { useDeskSort } from '@/hooks/useDeskSort'
import { useListPageSize } from '@/hooks/useListPageSize'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { resolveOffsetPagination } from '@/lib/list-pagination'
import { DESK_REFETCH_MS, isFilterChangeFetching, visibleTabRefetch } from '@/lib/query-client'
import { FILTER_BAR } from '@/lib/ui-shell'

const STATUS_OPTS = ['PENDING', 'MATCHED', 'PROCESSING', 'SERVER_PROCESSING', 'APPROVED', 'REJECTED']
const KIND_OPTS = ['DEPOSIT', 'WITHDRAW', 'TRANSFER']
const FUND_COLS = ['Created', 'Reference', 'Account', 'Name', 'IB', 'Team', 'Ticket', 'Type', 'Status', 'Method', 'Confirmed', 'Amount', 'Currency']

const FUND_SORT = ['created', 'amount', 'kind', 'status', 'ref', 'acct', 'method', 'confirmed', 'name'] as const
const ibTeam = (raw?: string | null) => {
  const { ib, team } = splitIb(raw)
  return ib === '—' && team === '—' ? '—' : `${ib} / ${team}`
}
const clip = 'overflow-hidden'
const stacked = 'overflow-hidden whitespace-normal'

/** Login over its platform; the header already says Account, so "MT5 Account (n)" is not repeated per row. */
function LoginCell({ login }: { login?: string | null }) {
  if (!login) return muted('—')
  return (
    <div className="overflow-hidden" title={mtAccount(login)}>
      <div className="truncate font-mono tabular-nums">{login}</div>
      <div className="text-[12.5px] text-muted-foreground">MT5</div>
    </div>
  )
}

/** Decision fields first: who, how much, what state. */
export const fundColumns: Column<FundQueueItem>[] = [
  {
    key: 'name',
    sortKey: 'name',
    header: 'Name',
    sticky: true,
    width: FUND_COLUMN_WIDTH.name,
    className: clip,
    mobile: 'title',
    cell: (r) => (
      <>
        <div className="truncate font-semibold" title={r.full_name || undefined}>{muted(dash(r.full_name))}</div>
        <div className="truncate font-mono text-[12.5px] text-muted-foreground" title={r.client_code || undefined}>{dash(r.client_code)}</div>
      </>
    ),
  },
  { key: 'amount', sortKey: 'amount', sortFirst: 'desc', header: 'Amount', align: 'right', width: FUND_COLUMN_WIDTH.amount, mobile: 'amount', cell: (r) => fmtMoney(r.amount, r.currency || '') },
  { key: 'status', sortKey: 'status', header: 'Status', width: FUND_COLUMN_WIDTH.status, className: 'whitespace-normal', mobile: 'status', cell: (r) => <StatusBadge className="whitespace-normal" status={r.status} /> },
  { key: 'kind', sortKey: 'kind', header: 'Type', width: FUND_COLUMN_WIDTH.kind, mobile: 'subtitle', cell: (r) => statusLabel(r.kind) },
  { key: 'created', sortKey: 'created', sortFirst: 'desc', header: 'Created', width: FUND_COLUMN_WIDTH.created, className: stacked, cell: (r) => <StackedDateTime value={r.created_at} />, mobile: 'field' },
  { key: 'acct', sortKey: 'acct', header: 'Account', width: FUND_COLUMN_WIDTH.acct, className: clip, mobile: 'field', cell: (r) => <LoginCell login={r.mt_login} /> },
  { key: 'ref', sortKey: 'ref', header: 'Reference', width: FUND_COLUMN_WIDTH.ref, className: clip, cell: (r) => <span className="block truncate font-mono" title={r.reference_code || undefined}>{muted(dash(r.reference_code))}</span> },
  { key: 'method', sortKey: 'method', header: 'Method', width: FUND_COLUMN_WIDTH.method, className: clip, cell: (r) => <span className="block truncate" title={methodLabel(r.transfer_method)}>{muted(methodLabel(r.transfer_method))}</span> },
  { key: 'ib', header: 'IB / team', headerTitle: 'Introducing broker / team', width: FUND_COLUMN_WIDTH.ib, className: clip, cell: (r) => <span className="block truncate" title={ibTeam(r.ib_team)}>{muted(ibTeam(r.ib_team))}</span> },
  { key: 'confirmed', sortKey: 'confirmed', sortFirst: 'desc', header: 'Confirmed', width: FUND_COLUMN_WIDTH.confirmed, className: stacked, cell: (r) => <StackedDateTime value={r.confirmed_at} /> },
]

export const FUND_TABLE_CLASS = 'table-fixed w-full'
/** Read-only field list shared by the Funds and First deposits detail sheets. */
/** `withSummary={false}` drops Type, Amount and Status when a header above already shows them. */
export function FundDetailList({ row, withSummary = true }: { row: FundQueueItem; withSummary?: boolean }) {
  const items: [string, ReactNode][] = [
    ...(withSummary
      ? ([
          ['Type', statusLabel(row.kind)],
          ['Amount', fmtMoney(row.amount, row.currency || '')],
          ['Status', <StatusBadge key="s" status={row.status} />],
        ] as [string, ReactNode][])
      : []),
    ['Reference', <span key="ref" className="font-mono">{dash(row.reference_code)}</span>],
    ['Account', <span key="acct" className="font-mono">{mtAccount(row.mt_login)}</span>],
    ['Ticket', <span key="ticket" className="font-mono">{dash(row.mt_ticket)}</span>],
    ['Method', methodLabel(row.transfer_method)],
    ['IB / team', ibTeam(row.ib_team)],
    ['Created', fmtDateTime(row.created_at)],
    ['Confirmed', fmtDateTime(row.confirmed_at)],
    ['Reason', row.unmatched_reason ? <ReasonText key={row.id} text={row.unmatched_reason} /> : dash(row.unmatched_reason)],
  ]
  return (
    <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-sm">
      {items.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="break-words">{v}</dd>
        </div>
      ))}
    </dl>
  )
}
const canDecide = (r: FundQueueItem) => r.status === 'PENDING' || r.status === 'MATCHED'

/** Long reasons show three lines, so the docked panel fits the window without its own scrollbar. */
function ReasonText({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  const [clamped, setClamped] = useState(false)
  const ref = useRef<HTMLParagraphElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (el && !open) setClamped(el.scrollHeight > el.clientHeight + 1)
  }, [text, open])
  return (
    <>
      <p ref={ref} className={open ? undefined : 'line-clamp-3'}>
        {text}
      </p>
      {clamped || open ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="mt-0.5 rounded-sm text-[13px] font-semibold text-link underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
        >
          {open ? 'Show less' : 'Show full reason'}
        </button>
      ) : null}
    </>
  )
}

function fundCsvRow(row: FundQueueItem): string[] {
  const ib = splitIb(row.ib_team)
  return [
    fmtTime(row.created_at),
    dash(row.reference_code),
    mtAccount(row.mt_login),
    dash(row.full_name),
    ib.ib,
    ib.team,
    dash(row.mt_ticket),
    statusLabel(row.kind),
    statusLabel(row.status),
    methodLabel(row.transfer_method),
    fmtTime(row.confirmed_at),
    String(row.amount),
    row.currency || '',
  ]
}

function FundDecision({
  row,
  onClose,
  onApprove,
  onReject,
  onPrev,
  onNext,
}: {
  row: FundQueueItem
  onClose: () => void
  onApprove: () => void
  onReject: () => void
  onPrev?: () => void
  onNext?: () => void
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg leading-6 font-semibold break-words">{dash(row.full_name)}</h2>
          <p className="font-mono text-[12.5px] text-muted-foreground">{dash(row.client_code)}</p>
        </div>
        <div className="flex shrink-0 items-center">
          {onPrev || onNext ? (
            <>
              <Button variant="ghost" size="icon" disabled={!onPrev} onClick={onPrev} aria-label="Previous request" title="Previous request">
                <ChevronLeft aria-hidden />
              </Button>
              <Button variant="ghost" size="icon" disabled={!onNext} onClick={onNext} aria-label="Next request" title="Next request">
                <ChevronRight aria-hidden />
              </Button>
            </>
          ) : null}
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close request detail">
            Close
          </Button>
        </div>
      </div>
      <div className="space-y-1 border-y border-border py-3">
        <p className="text-sm text-muted-foreground">{statusLabel(row.kind)}</p>
        <p className="font-mono text-2xl leading-[30px] font-semibold tabular-nums">{fmtMoney(row.amount, row.currency || '')}</p>
        <StatusBadge status={row.status} />
      </div>
      <FundDetailList row={row} withSummary={false} />
      {/* Pinned to the bottom of the panel or sheet, so the decision never sits below the fold.
          No negative bottom margin: it hid the last rows from the scroll height and the footer overlapped them. */}
      <div className="sticky bottom-0 -mx-5 mt-6 border-t border-border bg-card px-5 py-3.5">
      {canDecide(row) ? (
        <div className="space-y-2.5">
          {row.status === 'PENDING' ? (
            <p className="text-[13px] text-muted-foreground">No BCA line matches this request yet, so approving asks for a reason.</p>
          ) : null}
          <div className="flex gap-2.5">
            <Button className="h-10 flex-1" onClick={onApprove}>
              Approve
            </Button>
            <Button className="h-10 flex-1" variant="destructive" onClick={onReject}>
              Reject
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No decision needed. This request is {statusLabel(row.status).toLowerCase()}.</p>
      )}
      </div>
    </div>
  )
}

export function FundsQueuePage() {
  const qc = useQueryClient()
  const [sp, setSp] = useSearchParams()
  const { sort, onSortChange, sortQuery } = useDeskSort(FUND_SORT)
  const page = Number(sp.get('page') || '1')
  const pageSize = useListPageSize()
  const [draftQ, setDraftQ] = useState(sp.get('q') || '')
  const appliedQ = sp.get('q') || ''
  const status = facetFromSearch(sp, 'status')
  const kind = facetFromSearch(sp, 'kind')
  const [open, setOpen] = useState<FundQueueItem | null>(null)
  const [confirm, setConfirm] = useState<'approve' | 'reject' | null>(null)
  const rowRef = useRef<HTMLElement | null>(null)
  // Wide screens keep the queue in view next to the request; narrower ones use the slide-over sheet.
  const docked = useMediaQuery('(min-width: 1280px)')
  const params = {
    q: appliedQ || undefined,
    page,
    page_size: pageSize,
    include_total: true,
    ...sortQuery,
    ...facetToQuery('status', status),
    ...facetToQuery('kind', kind),
  }
  const q = useQuery({
    queryKey: ['funds-book', params],
    queryFn: () => backofficeApi.fundsBook(params),
    placeholderData: keepPreviousData,
    ...visibleTabRefetch(DESK_REFETCH_MS),
  })
  const decide = useMutation({
    mutationFn: (p: { id: string; approve: boolean; reason: string }) =>
      backofficeApi.fundsDecide(p.id, p.approve, p.reason || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['funds-book'] })
      void qc.invalidateQueries({ queryKey: ['desk-summary'] })
      setConfirm(null)
      setOpen(null)
    },
  })
  const items = q.data?.items ?? []
  const paging = resolveOffsetPagination({
    page,
    pageSize,
    total: q.data?.total,
    rowCount: items.length,
    hasMore: Boolean(q.data?.has_more),
  })
  const openIndex = open ? items.findIndex((r) => r.id === open.id) : -1
  const step = (delta: number) => (openIndex >= 0 && items[openIndex + delta] ? () => setOpen(items[openIndex + delta]) : undefined)

  function patchFacets(nextStatus = status, nextKind = kind, qValue = appliedQ, nextPage = '1', size = pageSize) {
    const cur = new URLSearchParams()
    if (qValue) cur.set('q', qValue)
    writeFacet(cur, 'status', nextStatus)
    writeFacet(cur, 'kind', nextKind)
    cur.set('page', nextPage)
    cur.set('page_size', String(size))
    writeSort(cur, sort)
    setSp(cur, { replace: true })
  }

  const csv = useMemo(() => items.map(fundCsvRow), [items])
  const detail = open ? (
    <FundDecision
      row={open}
      onClose={() => setOpen(null)}
      onApprove={() => setConfirm('approve')}
      onReject={() => setConfirm('reject')}
      onPrev={step(-1)}
      onNext={step(1)}
    />
  ) : null

  // The docked panel sits beside the whole page, not just the table: it starts where the title starts (88px)
  // and pins at that same offset, so it never moves while the list scrolls.
  return (
    <div className="flex items-start gap-5">
    <div className="min-w-0 flex-1">
    <PageFrame
      title="Funds"
      description="Client deposit and withdrawal requests. Approving without a BCA match, or rejecting, needs a reason."
      actions={
        <Button variant="outline" onClick={() => downloadCsv('funds.csv', FUND_COLS, csv)}>
          Export CSV
        </Button>
      }
    >
      <FilterBarForm onSubmit={() => patchFacets(status, kind, draftQ)} className={FILTER_BAR}>
        <div className="flex max-lg:flex-col max-lg:gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:gap-3">
          <FilterSearch value={draftQ} onChange={setDraftQ} placeholder="Name, code, login, reference" />
          <FacetFilter label="Status" value={status} options={optionLabels(STATUS_OPTS, statusLabel)} onChange={(next) => patchFacets(next, kind, appliedQ)} />
          <FacetFilter label="Type" value={kind} options={optionLabels(KIND_OPTS, statusLabel)} onChange={(next) => patchFacets(status, next, appliedQ)} />
          <FilterBarApply
            isDirty={draftQ !== appliedQ}
            canReset={Boolean(draftQ || appliedQ || status.values.length || kind.values.length)}
            onReset={() => {
              setDraftQ('')
              patchFacets(emptyFacet(), emptyFacet(), '')
            }}
          />
        </div>
      </FilterBarForm>
          <DeskTable
            columns={fundColumns}
            tableClassName={FUND_TABLE_CLASS}
            visibleKeys={fundColumnsForWidth}
            rows={items}
            rowKey={(r) => r.id}
            selectedKey={open?.id}
            onRowClick={(row, el) => {
              rowRef.current = el
              setOpen(row)
            }}
            loading={q.isLoading}
            error={q.error}
            dimmed={isFilterChangeFetching(q.isFetching, q.isPlaceholderData)}
            emptyTitle="No fund requests"
            emptyDescription="Nothing matches the search and filters above. Reset them to see every request."
            sort={sort}
            onSortChange={onSortChange}
            paging={{
              total: paging.totalItems,
              page: paging.safePage,
              pageSize,
              onPage: (p) => patchFacets(status, kind, appliedQ, String(p)),
              onPageSize: (size) => patchFacets(status, kind, appliedQ, '1', size),
            }}
          />
      {docked ? null : (
        <Sheet open={Boolean(open)} onClose={() => setOpen(null)} label="Fund request detail" returnFocusRef={rowRef}>
          {detail}
        </Sheet>
      )}
      <ConfirmDialog
        open={Boolean(open && confirm)}
        tone={confirm === 'reject' ? 'danger' : 'primary'}
        title={confirm === 'reject' ? 'Reject this request?' : 'Approve this request?'}
        summary={
          open ? (
            <>
              {dash(open.full_name)}: {statusLabel(open.kind)} of <strong className="text-foreground">{fmtMoney(open.amount, open.currency || '')}</strong>
              {confirm === 'approve' && open.status === 'PENDING' ? '. No BCA match was found, so a reason is required.' : '.'}
            </>
          ) : null
        }
        confirmLabel={confirm === 'reject' ? 'Reject' : 'Approve'}
        reason={
          confirm === 'reject'
            ? { label: 'Reason for rejecting', minLength: 3 }
            : open?.status === 'PENDING'
              ? { label: 'Reason for approving without a BCA match', minLength: 3 }
              : undefined
        }
        pending={decide.isPending}
        error={decide.error ? (decide.error as Error).message : null}
        onCancel={() => {
          decide.reset()
          setConfirm(null)
        }}
        returnFocusRef={rowRef}
        onConfirm={(reason) => open && confirm && decide.mutate({ id: open.id, approve: confirm === 'approve', reason })}
      />
    </PageFrame>
    </div>
    {docked ? (
      <aside
        aria-label="Request detail"
        className="sticky top-22 max-h-[calc(100dvh-6.5rem)] w-[clamp(300px,28vw,420px)] shrink-0 overflow-y-auto rounded-lg border border-border bg-card p-5"
      >
        {detail ?? (
          <div role="status" className="space-y-1.5">
            <p className="font-semibold">No request selected</p>
            <p className="text-sm text-muted-foreground">Select a row in the list to see the request and decide on it.</p>
          </div>
        )}
      </aside>
    ) : null}
    </div>
  )
}
